/**
 * @description A hook that handles the delete mapping definition action. It provides a function that, when called,
 *  confirms the action, dispatches a request to delete the mapping definition and then handles the success/failure.
 *  It can delete individual mapping definitions when an ID is passed in or all of them when no id is passed in.
 */

import React from 'react';

import decodeJson from '@seccon/shared/utils/decodeJson';
import { ToastContext } from '@seccon/core/contexts/toast';
import { ConfirmDialogContext } from '@seccon/core/contexts/confirm';

/**
 * @param {() => void} handleRefresh - a function that handles refreshing the mapping definition data set
 * @returns {({ id: string, name: string }) => void} - a callback function that handles the delete button click
 */
const useDeleteMappingDefinitions = (handleRefresh) => {
  const toastApi = React.useContext(ToastContext);
  const confirm = React.useContext(ConfirmDialogContext);

  /**
   * Handles the delete action by running the request and handling the success/failure response
   * @param {string} id - the id of the mapping definition to delete (if omitted, it deletes all records)
   */
  function handleAction (id) {
    /**
     * Handles sucess responses by notifying the user and refreshing the data set
     */
    function handleSuccess () {
      // Serve a different message depending on we're deleting one or all mapping definitions
      const successMsg = id ? (
        'Personnel sync mapping definition was deleted successfully.'
      ) : (
        'All personnel sync mapping definitions were deleted successfully.'
      )

      toastApi.success(successMsg);

      handleRefresh();
    }

    /**
     * Handles error responses by notifying the user
     * @param {{ message?: string }} err - the error response body (if any)
     */
    function handleFailure (err) {
      // Serve a different message depending on we're deleting one or all mapping definitions
      const errorMsg = id ? (
        'Personnel sync mapping definition could not be deleted. Try again later.'
      ) : (
        'Personnel sync mapping definitions could not be deleted. Try again later.'
      )

      // If an error message was included in the response, use that. Otherwise, use the generic message
      toastApi.error(err?.message ? `Error: ${ err.message }` : errorMsg);
    }

    // Call the appropriate API to delete one or all mapping definitions based on if an id was passed in
    fetch(id ? `/mapping/${ id }` : '/mappings', { method: 'DELETE' })
      .then(decodeJson)
      .then(handleSuccess)
      .catch(handleFailure);
  }

  /**
   * A callback function that handles the delete button click
   * @param {string} id - id of the mapping definition to delete (if omitted, then all definitions are to be deleted)
   * @param {string} name - name of the mapping definition to delete (omitted, if all definitions are to be deleted)
   */
  return function handleDelete (id, name) {
    // Set different confirmation dialog titles and messages depending if all definitions are selected or only one
    const confirmTitle = !name ? 'Delete All Mapping Definitions' : 'Delete Mapping Definition';
    const confirmMessage = !name ? (
      'Are you sure you want to delete all personnel mapping definitions?'
    ) : (
      `Are you sure you want to delete the personnel mapping "${ name }"?`
    );

    // Confirm the action and then run the action handler
    confirm(confirmTitle, confirmMessage, () => handleAction(id))
  }
};

export default useDeleteMappingDefinitions;
